import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { API_CONFIG } from '@/config/api';
import { updateLastSavedAt } from '../indexedDB/qsheetsDb';
import { updateLastSaveInMeta } from '../indexedDB/qsheetsMetaDb';
import { handleQEUpdate, setDeltaQE } from '../reducers/editor/actions';
import { store } from '../store';
import { storeLogsInLocalStorage } from '../utils/errors';
import { createSaveWorker } from './createSaveWorker';

interface WorkerSheetSaveMessage {
  type: 'sheet-data-save';
  response_status: number;
  tab_id: string;
  ref_id: string;
  savedAt: number;
  data?: unknown;
  from?: string;
  saving_workspace_id?: string;
  error?: unknown;
  errorResponseMessage?: string;
  payload?: unknown;
  traceID?: string;
}

interface WorkerMetaUploadMessage {
  type: 'meta-upload';
  response_status: number;
  savedAt: number;
  workspace_id?: string;
  from?: string;
  error?: unknown;
  errorResponseMessage?: string;
  payload?: unknown;
  traceID?: string;
}

interface WorkerRetryMessage {
  type: 'show-api-retry';
  payload?: unknown;
}

interface WorkerSaveRemainingMessage {
  saveRemaining: boolean;
}

type WorkerMessage =
  | WorkerSheetSaveMessage
  | WorkerMetaUploadMessage
  | WorkerRetryMessage
  | WorkerSaveRemainingMessage;

/**
 * useEditorAutoSave — manages the editorSaveWorker lifecycle.
 *
 * Creates a Web Worker on mount, posts a message every REACT_EDITOR_SAVE_INTERVAL
 * seconds so the worker can diff IndexedDB timestamps and push dirty sheets/meta
 * to the backend.  Handles the worker's responses (success → update IndexedDB +
 * Redux, failure → toast / log).
 */
export default function useEditorAutoSave(): void {
  const workerRef = useRef<Worker | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    console.log('[AutoSave] Hook mounted — creating worker');
    console.log('[AutoSave] EDITOR_SAVE_INTERVAL:', API_CONFIG.editorSaveInterval, 'seconds');

    // Create the worker
    let worker: Worker;
    try {
      worker = createSaveWorker();
      console.log('[AutoSave] Worker created successfully');
    } catch (err) {
      console.error('[AutoSave] FAILED to create worker:', err);
      return;
    }
    workerRef.current = worker;

    // Build the same base URL that thunks.ts uses.
    // In dev: API_CONFIG.baseUrl is '' (empty) → '/api/v1/' (relative, Vite proxy handles it)
    // In prod: API_CONFIG.baseUrl is the full backend URL → 'https://api.example.com/api/v1/'
    // The worker uses fetch() which resolves relative URLs against its own origin,
    // so relative URLs work correctly in both dev and prod.
    const configBase = API_CONFIG.baseUrl || '';
    const sheetsBaseUrl = `${configBase}${API_CONFIG.apiVersion}/`;

    let tickCount = 0;
    const postSave = () => {
      tickCount++;
      console.log(
        `[AutoSave] Interval tick #${tickCount} — posting message to worker with baseUrl:`,
        sheetsBaseUrl
      );
      worker.postMessage({
        baseUrl: sheetsBaseUrl,
      });
    };

    // Handle responses from the worker
    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const msg = event.data;
      console.log('[AutoSave] Received message from worker:', JSON.stringify(msg));

      if (!msg || typeof msg !== 'object') return;

      // Retry toast
      if ('type' in msg && msg.type === 'show-api-retry') {
        toast.error('Auto-save failed after multiple retries.', {
          autoClose: false,
          closeOnClick: false,
          onClose: () => {
            // Pause interval to prevent overlapping save attempts during reset
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            worker.postMessage({ resetFailedRetry: true });
            // Retrigger save after a short delay, then restart the interval.
            // Guard against double-creation: only restart if no interval exists.
            setTimeout(() => {
              postSave();
              if (!intervalRef.current) {
                intervalRef.current = setInterval(postSave, API_CONFIG.editorSaveInterval * 1000);
              }
            }, 1000);
          },
        });
        return;
      }

      // Successful meta upload
      if ('type' in msg && msg.type === 'meta-upload') {
        const m = msg as WorkerMetaUploadMessage;
        if (m.response_status >= 200 && m.response_status < 300) {
          console.log(
            '[AutoSave] Meta upload successful — savedAt:',
            m.savedAt,
            'response_status:',
            m.response_status
          );
          updateLastSaveInMeta(m.savedAt);
        } else {
          storeLogsInLocalStorage({
            source: 'editorAutoSave',
            type: 'meta-upload-error',
            status: m.response_status,
            message: m.errorResponseMessage,
            traceID: m.traceID,
          });
        }
        return;
      }

      // Successful sheet data save
      if ('type' in msg && msg.type === 'sheet-data-save') {
        const m = msg as WorkerSheetSaveMessage;
        if (m.response_status >= 200 && m.response_status < 300) {
          console.log(
            '[AutoSave] Sheet save successful — tab_id:',
            m.tab_id,
            'ref_id:',
            m.ref_id,
            'savedAt:',
            m.savedAt,
            'response_status:',
            m.response_status
          );
          store.dispatch(handleQEUpdate('querySheets', { last_saved_at: m.savedAt }, m.tab_id));
          store.dispatch(setDeltaQE(m.tab_id, false));
          updateLastSavedAt(m.tab_id, m.ref_id, 'editor', m.savedAt);
        } else {
          storeLogsInLocalStorage({
            source: 'editorAutoSave',
            type: 'sheet-save-error',
            status: m.response_status,
            message: m.errorResponseMessage,
            traceID: m.traceID,
          });
        }
        return;
      }
    };

    worker.onerror = (err) => {
      console.error('[AutoSave] Worker onerror fired:', err.message, err);
      console.warn(
        '[AutoSave] Worker error encountered — message:',
        err.message,
        'filename:',
        err.filename,
        'lineno:',
        err.lineno
      );
      storeLogsInLocalStorage({
        source: 'editorAutoSave',
        type: 'worker-error',
        message: err.message,
      });
    };

    // Start the interval — triggers the first save after REACT_EDITOR_SAVE_INTERVAL seconds
    intervalRef.current = setInterval(postSave, API_CONFIG.editorSaveInterval * 1000);

    // Cleanup
    return () => {
      console.log('[AutoSave] Hook unmounting — clearing interval and terminating worker');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      worker.terminate();
      workerRef.current = null;
    };
  }, []);
}
